import { FC } from "react";
import { useI18n } from "../../hooks/useTranslations";
import SvgCancel from "../icons/Cancel";
import SvgCheckCircle from "../icons/CheckCircle";
import SvgError from "../icons/Error";
import SvgInfo from "../icons/Info";
import Icon from "./Icon";
import ModalDialog from "./ModalDialog";
import styles from "./TableFileUploadDialog.module.css";

export type FileToOverwrite = {
  file: File;
  onCancel: () => void;
  onOverwrite: () => void;
};

export type TableFileUploadStatus = "pending" | "uploading" | "success" | "error" | "skipped";

export type TableFileUploadResult = {
  fileName: string;
  status: TableFileUploadStatus;
  errorMessage?: string;
};

export type TableUploadFileResult = "success" | "skipped" | void;

type TableFileUploadDialogProps = {
  isUploading: boolean;
  onClose: () => void;
  results: TableFileUploadResult[];
};

const getStatusIcon = (status: TableFileUploadStatus) => {
  switch (status) {
    case "success":
      return SvgCheckCircle;
    case "error":
      return SvgError;
    case "pending":
      return SvgInfo;
    case "skipped":
      return SvgCancel;
    case "uploading":
      return SvgInfo;
  }
};

const getStatusColor = (status: TableFileUploadStatus): "blue" | "green" | "grey" | "red" => {
  switch (status) {
    case "success":
      return "green";
    case "error":
      return "red";
    case "pending":
      return "grey";
    case "skipped":
      return "grey";
    case "uploading":
      return "blue";
  }
};

const TableFileUploadDialog: FC<TableFileUploadDialogProps> = ({ isUploading, onClose, results }) => {
  const i18n = useI18n();

  const statusLabels: Record<TableFileUploadStatus, string> = {
    error: i18n.t.common.error,
    pending: i18n.t.common.loading,
    skipped: i18n.t.common.cancelled,
    success: i18n.t.common.done,
    uploading: i18n.t.common.loading,
  };

  return (
    <ModalDialog
      title={i18n.t.common.file}
      isSubmitting={isUploading}
      onClose={() => {
        if (!isUploading) {
          onClose();
        }
      }}
      actions={[
        {
          title: i18n.t.common.close,
          disabled: isUploading,
          onClick: onClose,
        },
      ]}
    >
      <ul className={styles.list}>
        {results.map((result, index) => (
          <li className={styles.item} key={`${result.fileName}_${index}`}>
            <Icon glyph={getStatusIcon(result.status)} color={getStatusColor(result.status)} className={styles.icon} />
            <span className={styles.status}>{statusLabels[result.status]}</span>
            <span className={styles.fileName}>{result.fileName}</span>
            {!!result.errorMessage && <span className={styles.errorMessage}>{result.errorMessage}</span>}
          </li>
        ))}
      </ul>
    </ModalDialog>
  );
};

export default TableFileUploadDialog;
